import { listingFacade } from "../interaction/facades/listing.facade.instance.js";
import * as listingDraftStore from "../interaction/stores/listing-draft.store.js";
import { filterSelectedKeywordsForSearchTerms } from "../keyword-policy-client.js";
import { buildVolumeMap } from "../keyword-lookup.js";
import { getByteSize } from "../byte-counter.js";
import { selectKeywordConflictState } from "../state-selectors/listing-selectors.js";
import { buildListingRevisionTextHash } from '../interaction/builders/listing-draft-normalizers.js';

function normalizeStringList(values) {
  return (Array.isArray(values) ? values : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function splitFinalizedSearchTerms(value) {
  return String(value || "")
    .replace(/[,;#]+/g, " ")
    .split(/\s+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isListingV4FinalizedSearchTerms(payload) {
  return String(payload?.source || "") === "listing_v4_code_finalizer";
}

function buildFinalizedSearchTermsDraft(aiSearchTerms, volumeMap, approvedKeywords = []) {
  const tokens = splitFinalizedSearchTerms(aiSearchTerms?.searchTermsEn || aiSearchTerms?.searchTerms || aiSearchTerms?.text);
  const enriched = tokens.map((keyword) => ({
    keyword,
    searches30d: volumeMap.get(keyword.toLowerCase()) || 0,
  }));
  return {
    ...aiSearchTerms,
    searchTermsEn: tokens.join(" "),
    keywords: enriched,
    candidates: tokens,
    selected: tokens,
    approvedKeywords: normalizeStringList(approvedKeywords),
    error: null,
  };
}

export function buildSearchTermsWithUserPriority(aiSearchTerms, deps) {
  const MAX_BYTES = 250;

  const userSelected = deps.getState('selectedStKeywords');
  const userPacked = deps.getState('step02SearchTermsSelectionDraft')
    || deps.getState('listingDraftSearchTerms');

  // 共享 volume map：一次读 state，覆盖 keywordLibrary + market data 两个来源
  const volumeMap = buildVolumeMap();
  if (isListingV4FinalizedSearchTerms(aiSearchTerms)) {
    const approvedKeywords = userSelected?.size > 0
      ? [...userSelected]
      : (userPacked?.approvedKeywords || userPacked?.selected || []);
    return buildFinalizedSearchTermsDraft(aiSearchTerms, volumeMap, approvedKeywords);
  }

  const aiCandidates = normalizeStringList(aiSearchTerms?.candidates || []);

  const enriched = [];
  let usedBytes = 0;
  const userKwList = normalizeStringList(
    userSelected?.size > 0 ? [...userSelected] : (userPacked?.candidates || [])
  );
  const resolvedPolicies = deps.getState('keywordUsePolicies') || deps.getState('businessContext')?.keywordUsePolicies || [];
  const filteredUserKwList = filterSelectedKeywordsForSearchTerms(userKwList, resolvedPolicies);
  const conflictState = selectKeywordConflictState({
    keywordConflictResult: deps.getState('keywordConflictResult'),
    lastDetectedConflicts: deps.getState('lastDetectedConflicts'),
    batchJob: deps.getState('batchJob'),
  });
  const aiBlacklist = new Set(
    conflictState.conflicts
      .filter((c) => c.keyword)
      .map((c) => String(c.keyword).toLowerCase().trim())
  );

  for (const kw of filteredUserKwList) {
    if (aiBlacklist.has(kw.toLowerCase().trim())) continue;
    const kwBytes = getByteSize(kw);
    const sep = enriched.length > 0 ? 2 : 0;
    if (usedBytes + sep + kwBytes > MAX_BYTES) break;
    enriched.push({ keyword: kw, searches30d: volumeMap.get(kw.toLowerCase()) || 0 });
    usedBytes += sep + kwBytes;
  }

  const usedSet = new Set(enriched.map((e) => e.keyword.toLowerCase()));

  for (const kw of aiCandidates) {
    const kwLower = kw.toLowerCase().trim();
    if (usedSet.has(kwLower)) continue;
    if (aiBlacklist.has(kwLower)) continue;
    const kwBytes = getByteSize(kw);
    const sep = enriched.length > 0 ? 2 : 0;
    if (usedBytes + sep + kwBytes > MAX_BYTES) break;
    enriched.push({ keyword: kw, searches30d: volumeMap.get(kwLower) || 0 });
    usedBytes += sep + kwBytes;
    usedSet.add(kwLower);
  }

  const searchTermsEn = enriched.map((e) => e.keyword).join(' ');
  return {
    searchTermsEn,
    keywords: enriched,
    candidates: enriched.map((e) => e.keyword),
    selected: enriched.map((e) => e.keyword),
    error: null,
  };
}

function sectionStatus(generated, name) {
  return String(generated?.meta?.sections?.[name]?.status || '').trim().toLowerCase();
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function candidateHasText(value) {
  if (hasText(value) || typeof value === 'number') return true;
  if (!value || typeof value !== 'object') return false;
  return [
    value.textEn,
    value.text,
    value.keyword,
    value.term,
    value.questionEn,
    value.answerEn,
    value.question,
    value.answer,
  ].some(hasText);
}

function listHasText(values) {
  return Array.isArray(values) && values.some(candidateHasText);
}

function hasUsableTitle(title) {
  if (!title || typeof title !== 'object') return false;
  return [
    title.productTitle,
    title.titleMain,
    title.titleEn,
    title.itemHighlights,
    title.titleSub,
  ].some(hasText) || listHasText(title.titleOptions);
}

function hasUsableBullets(bullets) {
  if (!bullets || typeof bullets !== 'object') return false;
  return listHasText(bullets.bulletsEn || bullets.bullets);
}

function hasUsableDescriptionCopy(description) {
  if (!description || typeof description !== 'object') return false;
  return [description.descriptionEn, description.descriptionZh, description.description]
    .some(hasText);
}

function hasUsableAplus(description) {
  return Array.isArray(description?.aplusModules)
    && description.aplusModules.some((module) => (
      module
      && typeof module === 'object'
      && [module.headline, module.bodyText].some(hasText)
    ));
}

function hasUsableSearchTerms(searchTerms) {
  if (!searchTerms || typeof searchTerms !== 'object') return false;
  return [searchTerms.searchTermsEn, searchTerms.searchTerms, searchTerms.text].some(hasText)
    || listHasText(searchTerms.candidates)
    || listHasText(searchTerms.selected)
    || listHasText(searchTerms.keywords);
}

function hasUsableQa(qa) {
  const rows = Array.isArray(qa) ? qa : qa?.qa;
  return Array.isArray(rows) && rows.some((row) => (
    row
    && typeof row === 'object'
    && [row.questionEn, row.answerEn, row.questionZh, row.answerZh, row.question, row.answer].some(hasText)
  ));
}

function shouldApplySection(generated, name, hasUsableData) {
  return sectionStatus(generated, name) === 'ok' || hasUsableData;
}

function mergeDescriptionDraft(current, incoming, { copyChanged, aplusChanged, replaceAll }) {
  if (replaceAll) return incoming;
  if (!copyChanged && !aplusChanged) return current;

  const previous = current && typeof current === 'object' ? current : {};
  const next = {
    ...previous,
    ...(incoming && typeof incoming === 'object' ? incoming : {}),
  };

  if (!copyChanged) {
    next.descriptionEn = previous.descriptionEn || '';
    next.descriptionZh = previous.descriptionZh || '';
    if (Object.hasOwn(previous, 'description')) next.description = previous.description;
  }
  if (!aplusChanged) {
    next.aplusModules = Array.isArray(previous.aplusModules) ? previous.aplusModules : [];
  }
  return next;
}

function mergePartialV1({ current, incoming, effective, changed, preservedAnyField }) {
  if (!preservedAnyField) return incoming;
  if (!current && !incoming) return null;

  const next = {
    ...(current && typeof current === 'object' ? current : {}),
    ...(incoming && typeof incoming === 'object' ? incoming : {}),
  };
  for (const name of ['title', 'bullets', 'description', 'searchTerms', 'qa']) {
    if (!changed[name] || !incoming?.[name]) {
      next[name] = effective[name];
    }
  }
  return next;
}

export function applyGenerateAllResult(data, jobId, deps) {
  const current = {
    title: deps.getState('listingDraftTitle'),
    bullets: deps.getState('listingDraftBullets'),
    description: deps.getState('listingDraftDescription'),
    searchTerms: deps.getState('listingDraftSearchTerms'),
    qa: deps.getState('listingDraftQa'),
    v1: deps.getState('listingDraftV1'),
    optimizerResult: deps.getState('optimizerResult'),
  };
  // Normalize first without mutating the shared Store. A partial/error response
  // must never create a transient empty draft before field-level preservation runs.
  const generated = listingFacade.applyGenerationResult(data, { writeToStore: false });
  const titleChanged = shouldApplySection(generated, 'title', hasUsableTitle(generated.title));
  const bulletsChanged = shouldApplySection(generated, 'bullets', hasUsableBullets(generated.bullets));
  const descriptionOk = sectionStatus(generated, 'description') === 'ok';
  const descriptionCopyChanged = descriptionOk || hasUsableDescriptionCopy(generated.description);
  const aplusChanged = descriptionOk || hasUsableAplus(generated.description);
  // An empty code-owned Search Terms payload is a valid "no new safe terms"
  // outcome, but it is not replacement content. Preserve the current safe
  // backend terms until SearchTeam returns a non-empty result.
  const searchTermsChanged = hasUsableSearchTerms(generated.searchTerms);
  const qaChanged = shouldApplySection(generated, 'qa', hasUsableQa(generated.qa));
  const searchTermsDraft = searchTermsChanged
    ? buildSearchTermsWithUserPriority(generated.searchTerms, deps)
    : current.searchTerms;
  const effective = {
    title: titleChanged ? generated.title : current.title,
    bullets: bulletsChanged ? generated.bullets : current.bullets,
    description: mergeDescriptionDraft(current.description, generated.description, {
      copyChanged: descriptionCopyChanged,
      aplusChanged,
      replaceAll: descriptionOk,
    }),
    searchTerms: searchTermsDraft,
    qa: qaChanged ? generated.qa : current.qa,
  };
  const changed = {
    title: titleChanged,
    bullets: bulletsChanged,
    // V1 stores Description and A+ together. It is fully replaceable only when
    // both nested fields were accepted; otherwise write the effective merge.
    description: descriptionCopyChanged && aplusChanged,
    searchTerms: searchTermsChanged,
    qa: qaChanged,
  };
  const preservedAnyField = !titleChanged
    || !bulletsChanged
    || !descriptionCopyChanged
    || !aplusChanged
    || !searchTermsChanged
    || !qaChanged;
  const effectiveV1 = mergePartialV1({
    current: current.v1,
    incoming: generated.v1,
    effective,
    changed,
    preservedAnyField,
  });
  const effectiveOptimizerResult = preservedAnyField && generated.optimizerResult == null
    ? current.optimizerResult
    : generated.optimizerResult;
  const draftForReviewHash = {
    title: effective.title,
    bullets: effective.bullets,
    description: effective.description,
    searchTerms: searchTermsDraft,
    qa: effective.qa,
  };
  const generatedMeta = generated.meta?.mergeMeta?.draftSelfReview
    ? {
        ...generated.meta,
        mergeMeta: {
          ...generated.meta.mergeMeta,
          draftSelfReview: {
            ...generated.meta.mergeMeta.draftSelfReview,
            listingTextHash: buildListingRevisionTextHash(draftForReviewHash),
          },
        },
      }
    : generated.meta;
  listingDraftStore.replaceDraft({
    listingDraftTitle: effective.title,
    listingDraftBullets: effective.bullets,
    listingDraftDescription: effective.description,
    listingDraftSearchTerms: searchTermsDraft,
    listingDraftQa: effective.qa,
    listingDraftV1: effectiveV1,
    optimizerResult: effectiveOptimizerResult,
  });
  const partialErrors = [
    ...generated.rawErrors,
    ...generated.sectionErrors
      .filter((item) => item.type !== 'warning')
      .map((item) => `${deps.sectionLabel(item.section)}: ${item.message}`),
  ].filter(Boolean);
  const stepReady = deps.getState('stepReady') || {};
  const statePatch = {
    listingDraftTitle: effective.title,
    listingDraftBullets: effective.bullets,
    listingDraftDescription: effective.description,
    listingDraftSearchTerms: searchTermsDraft,
    listingDraftQa: effective.qa,
    listingGenerationMeta: generatedMeta,
    listingGenerationSectionErrors: generated.sectionErrors,
    complianceResult: null,
    scoringReport: null,
    revisionSuggestions: [],
    revisionSuggestionsError: '',
    stepReady: { ...stepReady, 4: false },
    listingDraftV1: effectiveV1,
    optimizerResult: effectiveOptimizerResult,
  };
  if (titleChanged) {
    Object.assign(statePatch, {
      titleShowingZh: false,
      titleZhMap: {},
      titleHighlightZhMap: {},
    });
  }
  if (bulletsChanged) {
    Object.assign(statePatch, {
      bulletsShowingZh: false,
      bulletsZhMap: {},
    });
  }
  if (descriptionCopyChanged) {
    Object.assign(statePatch, {
      descriptionShowingZh: false,
      descriptionZhText: '',
    });
  }
  if (aplusChanged) {
    Object.assign(statePatch, {
      aplusShowingZh: false,
      aplusZhMap: {},
    });
  }
  deps.setStateBulk(statePatch);
  deps.scheduleStep3AutoSave(jobId);
  return partialErrors;
}
